// deps
import { useState, useCallback, useEffect, useContext, useRef } from "react";

// hooks
import { captureError, format, useMountedRef } from "@swiggy-private/core-ui";

// contexts
import {
    ApiContext,
    getBridgeSdk,
    recordNREvent,
    CheckoutActionTypes,
    CheckoutContext,
    useNavigate,
    NEWRELIC_TABLES,
    navigateToCart,
} from "@swiggy-private/checkout-core";

// helpers
import { getDirectPlaceOrderParams, handlePlaceOrderSuccess } from "@payment-client/helpers/OrderHelper";

// interfaces
import { OrderData } from "@swiggy-private/payment-types";

// constants
import { API_SUCCESS_STATUS_CODE } from "@payment-client/constants/StatusCodes";
import {
    DISMISS,
    OOPS_SOMETHING_WENT_WRONG,
    TRANSACTION_FAILED,
    PAYMENT_ALERT_MAIN_TEXT,
} from "@payment-client/constants/TextDict";
import { PlaceOrderSuccessHandlerReturnType } from "@payment-client/interfaces/Order";
import { placeOrderPublic } from "@payment-client/apis/publicOrder";

export interface DirectPlaceOrderState {
    isDirectPlaceOrderSuccess: boolean;
    orderData?: OrderData;
}

type UseDirectPlaceOrderReturnType = [DirectPlaceOrderState, VoidFunction];
export interface DirectPlaceOrderProps {
    cartTotal: number;
    fromCart: boolean;
    orderComment: string;
}

const useDirectPlaceOrder = (props: DirectPlaceOrderProps): UseDirectPlaceOrderReturnType => {
    const { cartTotal, fromCart, orderComment } = props;
    const isMountedRef = useMountedRef();
    const apiContextValue = useContext(ApiContext);
    const apiCallRef = useRef(false);
    const { setCheckoutState } = useContext(CheckoutContext);
    const [directPlaceOrderReturnValue, setDirectPlaceOrderReturnValue] = useState<DirectPlaceOrderState>({
        isDirectPlaceOrderSuccess: false,
    });
    const [isOrderInProgress, setOrderInProgress] = useState(false);
    const apiHost = apiContextValue.host.unwrap();
    const navigateTo = useNavigate();

    const doDirectPlaceOrder = useCallback(() => {
        if (isMountedRef.current && !isOrderInProgress) {
            setOrderInProgress(true);
        }
    }, [isMountedRef, isOrderInProgress]);

    const handlePlaceOrderData = useCallback(
        ({ orderData }: PlaceOrderSuccessHandlerReturnType) => {
            if (!isMountedRef.current) {
                return;
            }

            setOrderInProgress(false);
            setDirectPlaceOrderReturnValue({
                orderData,
                isDirectPlaceOrderSuccess: true,
            });
        },
        [isMountedRef],
    );

    const handlePlaceOrderDataError = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (err: any) => {
            if (!isMountedRef.current) {
                return;
            }

            // recording failure in newrelic
            recordNREvent(
                {
                    eventType: NEWRELIC_TABLES.PAYMENTS_WEB_DIRECT_PLACE_ORDER_FAILURE,
                    failureReason: err.statusMessage ?? "Received error while direct place order",
                    statusCode: err.statusCode ?? 1,
                    fromCart: String(fromCart),
                },
                false,
            );

            getBridgeSdk()
                .directPlaceOrderFailure()
                .catch(() => void 0);

            if (fromCart) {
                if (err && err.statusCode !== API_SUCCESS_STATUS_CODE) {
                    setCheckoutState({
                        type: CheckoutActionTypes.GENERIC_PAYMENT_ALERT,
                        payload: {
                            info: format(PAYMENT_ALERT_MAIN_TEXT, String(cartTotal)),
                            primaryAction: {
                                text: DISMISS,
                                onClick: (): void => {
                                    return;
                                },
                            },
                            title: TRANSACTION_FAILED,
                            message: err.statusMessage || OOPS_SOMETHING_WENT_WRONG,
                        },
                    });

                    captureError(err);
                    navigateToCart(navigateTo);
                }
            }
        },
        [cartTotal, fromCart, isMountedRef, navigateTo, setCheckoutState],
    );

    useEffect(() => {
        // is mounted check..
        if (!isMountedRef.current || apiCallRef.current || !isOrderInProgress) {
            return;
        }

        setOrderInProgress(true);
        apiCallRef.current = true;

        const placeOrderParams = getDirectPlaceOrderParams(fromCart, cartTotal, orderComment);

        placeOrderPublic(apiHost, placeOrderParams)
            .then(res => handlePlaceOrderSuccess(res, NEWRELIC_TABLES.PAYMENTS_WEB_DIRECT_PLACE_ORDER))
            .then(handlePlaceOrderData)
            .catch(handlePlaceOrderDataError)
            .then(() => {
                if (isMountedRef.current) {
                    apiCallRef.current = false;
                    setOrderInProgress(false);
                }
            });
    }, [
        apiHost,
        cartTotal,
        fromCart,
        handlePlaceOrderData,
        handlePlaceOrderDataError,
        isMountedRef,
        isOrderInProgress,
        orderComment,
    ]);

    return [directPlaceOrderReturnValue, doDirectPlaceOrder];
};

export default useDirectPlaceOrder;
